import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useHomepagePageData } from 'src/app/pages/homepage/pageContext';

import type { TopStory } from '../../types';

type Result = ModelHookResult<
	{
		topStories: TopStory[];
	},
	{
		trackStoryClick: (url: string) => void;
	}
>;

export function useTopImpactStories(): Result {
	const track = useTracking();
	const {
		data: { topImpactStories },
	} = useHomepagePageData();

	const { translate, translatePlural } = useI18n();

	const topStories = topImpactStories.map((story, index) => {
		const labelTitle = story.isVictory
			? translate('corgi.homepage.top_impact_stories.badge.label_title_victory')
			: translate('corgi.homepage.top_impact_stories.badge.label_title_trending');

		const labelText = translatePlural(
			'corgi.homepage.top_impact_stories.badge.label_text',
			Number(story.signatureCount),
			{
				signatureCount: story.signatureCount,
			},
		);

		return {
			key: index + 1,
			image: story.imageUrl,
			altText: translate('corgi.homepage.top_impact_stories.avatar.alt_text', { index: index + 1, totalStories: 5 }),
			ariaLabel: [
				translate('corgi.homepage.top_impact_stories.aria_label', { index: index + 1, totalStories: 5 }),
				`${labelTitle} ${labelText}`,
				story.summary,
			].join('. '),
			url: `/impact-stories/${story.slug}?source_location=homepage_bubble`,
			summary: story.summary,
			labelTitle,
			labelText,
			dataTestIdBase: `top-impact-story-${index + 1}-of-5`,
		};
	});

	return {
		data: {
			topStories,
		},
		actions: {
			trackStoryClick: (url: string) => {
				void track('homepage_top_impact_story_click', { url });
			},
		},
	};
}
